/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';

import styles from 'src/shared/app/authentication/component/support/base/BaseSupport.style';

import SupportLink from 'src/shared/app/authentication/component/support/base/SupportLink';
import LazyLoginMain from 'src/shared/app/authentication/component/login/LazyLoginMain';
import messages from 'src/shared/app/authentication/l10n/authenticationSupportL10n';
import ZIndexTopBar from 'src/shared/app/base/component/template/component/TopBar';
import navMessages from 'src/shared/app/nav/l10n/navigationL10n';
import TopBar from 'src/shared/app/nav/component/top-bar/TopBar';
import useRoutes from 'src/shared/app/base/route/hook/useRoutes';
import { loginRoute } from 'src/shared/app/base/route/setting/routeSettings';
import CircledArrowIcon from 'src/shared/ui/component/icon/CircledArrowIcon';
import LockIcon from 'src/shared/ui/component/icon/LockIcon';
import QuestionIcon from 'src/shared/ui/component/icon/QuestionIcon';
import InstructionText from 'src/shared/ui/component/text/InstructionText';

type Props = {
  isLoginLinkDisabled: boolean,
  handleLinkClick: () => void,
  handleResetPasswordClick: () => void,
  handleContactClick: () => void,
};

const useStyles = createUseStyles(styles);

const PasswordSupportSection = ({
  isLoginLinkDisabled,
  handleLinkClick,
  handleResetPasswordClick,
  handleContactClick,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | Hooks
   |----------------------------------------------------------------------------
   */

  const intl = useIntl();
  const [loginPath] = useRoutes(loginRoute);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    LazyLoginMain.preload();
  }, []);

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const loginLinkElt = isLoginLinkDisabled ? null : (
    <SupportLink
      title={intl.formatMessage(messages.loginLinkTitle)}
      cta={intl.formatMessage(messages.loginLinkCta)}
      icon={<CircledArrowIcon />}
      handleClick={handleLinkClick}
    />
  );

  const resetLoginElt = isLoginLinkDisabled ? null : (
    <SupportLink
      title={intl.formatMessage(messages.resetPasswordTitle)}
      cta={intl.formatMessage(messages.resetPasswordCta)}
      icon={<LockIcon />}
      handleClick={handleResetPasswordClick}
    />
  );

  return (
    <section className={classes.section}>
      <ZIndexTopBar>
        <TopBar
          title={intl.formatMessage(messages.passwordHeading)}
          cancelLabel={intl.formatMessage(navMessages.cancel)}
          backTo={loginPath}
        />
      </ZIndexTopBar>
      <div className={classes.instruction}>
        <InstructionText>
          {intl.formatMessage(messages.description)}
        </InstructionText>
      </div>
      {loginLinkElt}
      {resetLoginElt}

      <SupportLink
        title={intl.formatMessage(messages.contactSupportTitle)}
        cta={intl.formatMessage(messages.contactSupportCta)}
        icon={<QuestionIcon />}
        handleClick={handleContactClick}
      />
    </section>
  );
};

export default compose(React.memo)(PasswordSupportSection);
